import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Fab, styled } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { TransitionGroup } from 'react-transition-group';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import AddConstructionDialog from './add-construction/add-construction-dialog.component';
import { ActionConstruction } from 'models/order-input/action-order';
import ConstructionBox from './construction-box.component';
import ModifyConstructionDialog from './modify-construction/modify-construction-dialog.component';
import DeleteConstructionConfirmationDialog from 'components/order-flow/shared/constructions/delete-construction-confirmation-dialog.component';

let animationIdCounter = 0;

type ConstructionProps = {
    disableEdit?: boolean;
};
const Constructions: FC<ConstructionProps> = ({ disableEdit }) => {
    const { t: translate } = useTranslation(['orders']);
    const { order, setOrder } = useContext(ActionOrderContext);
    const [constructionToModify, setConstructionToModify] = useState<ActionConstruction>();
    const [constructionIndexToModify, setConstructionIndexToModify] = useState<number>();
    const [addConstructionDialogOpen, setAddConstructionDialogOpen] = useState(false);
    const [modifyConstructionDialogOpen, setModifyConstructionDialogOpen] = useState(false);
    const [constructionIndexPendingDelete, setConstructionIndexPendingDelete] = useState<number>();

    const handleAddConstruction = (construction: ActionConstruction) => {
        const constructionWithAnimationId = {
            ...construction,
            animationId: construction.animationId || `anim-${Date.now()}-${animationIdCounter++}`,
        };
        setOrder((prev) => ({
            ...prev,
            constructions: [...prev.constructions, constructionWithAnimationId],
        }));
    };

    const handleDeleteConstruction = (index: number) => {
        setConstructionIndexPendingDelete(index);
    };

    const handleConfirmedDeleteConstruction = () => {
        if (constructionIndexPendingDelete !== undefined) {
            setOrder((prev) => {
                const constructions = [...prev.constructions];
                constructions.splice(constructionIndexPendingDelete, 1);
                return {
                    ...prev,
                    constructions,
                };
            });
        }
        setConstructionIndexPendingDelete(undefined);
    };

    const handleModifyConstructionClick = (index: number) => {
        const construction = order.constructions[index];
        setConstructionToModify({ ...construction });
        setConstructionIndexToModify(index);
        setModifyConstructionDialogOpen(true);
    };

    const handleModifyConstruction = (construction: ActionConstruction) => {
        if (constructionIndexToModify !== undefined) {
            setOrder((prev) => {
                const constructions = [...prev.constructions];
                const original = constructions[constructionIndexToModify];
                constructions[constructionIndexToModify] = {
                    ...construction,
                    _id: original._id,
                    animationId: original.animationId,
                    addedMaterials: original.addedMaterials,
                };
                return {
                    ...prev,
                    constructions,
                };
            });
            setConstructionToModify(undefined);
            setConstructionIndexToModify(undefined);
        }
    };

    return (
        <Fragment>
            <Box>
                <TransitionGroup component={null}>
                    {order.constructions.map((construction, index) => (
                        <Collapse key={construction.animationId || index}>
                            <Box mb={2} sx={{ display: 'flex' }}>
                                <ConstructionBox
                                    construction={construction}
                                    onDeleteClick={() => handleDeleteConstruction(index)}
                                    onModifyClick={() => handleModifyConstructionClick(index)}
                                />
                            </Box>
                        </Collapse>
                    ))}
                </TransitionGroup>
                {!disableEdit ? (
                    <AddConstructionButton
                        disableRipple={true}
                        onClick={() => setAddConstructionDialogOpen(true)}
                        variant="extended"
                    >
                        <AddIcon />
                        <Box>{translate('orders:ADD_CONSTRUCTION')}</Box>
                    </AddConstructionButton>
                ) : undefined}
            </Box>

            <AddConstructionDialog
                actionSet={order.currentActionSet}
                open={addConstructionDialogOpen}
                onClose={() => setAddConstructionDialogOpen(false)}
                onAddConstruction={handleAddConstruction}
            />
            <ModifyConstructionDialog
                constructionToModify={constructionToModify}
                actionSet={order.currentActionSet}
                open={modifyConstructionDialogOpen}
                onClose={() => setModifyConstructionDialogOpen(false)}
                onModifyConstruction={handleModifyConstruction}
            />
            <DeleteConstructionConfirmationDialog
                open={constructionIndexPendingDelete !== undefined}
                hasAddedMaterials={
                    constructionIndexPendingDelete !== undefined &&
                    !!order.constructions[constructionIndexPendingDelete]?.addedMaterials?.length
                }
                onCancel={() => setConstructionIndexPendingDelete(undefined)}
                onConfirm={handleConfirmedDeleteConstruction}
            />
        </Fragment>
    );
};

const AddConstructionButton = styled(Fab)(({ theme }) => ({
    background: 'rgba(235, 235, 235, 0.4)',
    color: theme.palette.primary.main,
    borderRadius: 5,
    width: '100%',
    minHeight: '150px',
    height: '100%',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
        background: 'rgba(235, 235, 235, 0.2)',
    },
    '& svg': {
        fontSize: 60,
        fill: theme.palette.primary.main,
        margin: 0,
        display: 'block',
    },
}));

export default Constructions;
